import React, { useMemo } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { computeFirstPartOfISOStringFromDayjs } from '../utils/parking.date.utils';
import { ParkingFilters } from '../../../domain/parking';
import { TIME_OFFSET, TIMEZONE } from '../utils';

dayjs.extend(utc);
dayjs.extend(timezone);


// ********************************************************************************
export const DEFAULT_PARKING_FILTERS_FORM_VALUES: ParkingFilters = {
  entryDate: '',
  entryTime: '',
  exitDate: '',
  exitTime: '',
  numberOfVehicles: 1,
};

export interface ParkingFiltersFormProps {
  formManager: UseFormReturn<ParkingFilters>;
}

export const ParkingFiltersForm = ({ formManager }: ParkingFiltersFormProps) => {
  const { t } = useTranslation();
  const { control, watch } = formManager;

  // == State =====================================================================
  const entryDate = watch('entryDate');
  const entryTime = watch('entryTime');
  const exitDate = watch('exitDate');
  // The minimum date is {TIME_OFFSET} hours from now on {TIMEZONE} timezone. The
  // user can't select a date before that time.
  // NOTE: minTime in TimePicker has the same format and functionality as minDate 
  //       in the DatePicker.
  const minDate = dayjs().tz(TIMEZONE).add(TIME_OFFSET, 'hours');
  let minHour = dayjs(minDate);
  if (entryDate) {
    const dayEntryDate = dayjs(entryDate);
    if (dayEntryDate.date() !== minDate.date() || dayEntryDate.month() !== minDate.month() || dayEntryDate.year() !== minDate.year()) {
      minHour = null;
    }
  }

  // Time picker value is a combination of entryDate and entryTime, this is made
  // to ensure that the time selected by the user is set to the same date as the
  // entry date (TimePicker doesn't have a date picker, so it defaults to the
  // current date if no date is provided)
  // NOTE: This value is not stored in the form, it's just a helper to get the
  //       right time value to pass to the TimePicker component.
  const timePickerValue = useMemo(() => {
    if (!entryDate) return null;
    if (!entryTime) return null; /*no selection*/;

    const date = dayjs(entryDate);
    const time = dayjs(entryTime).tz(TIMEZONE)/*timezone to the time*/;
    // Set the year, month and date of the time to the date selected by the user
    return time.set('date', date.date()).set('month', date.month()).set('year', date.year());
  }, [entryDate, entryTime]);

  // == Handlers ==================================================================
  const handleParkingDateChange = (onChange: (date: string) => void) => (date: Dayjs | null) => {
    if (!date) return;
    if (!date.isValid()) return;

    onChange(computeFirstPartOfISOStringFromDayjs(date));
  };

  const handleParkingTimeDateChange = (onChange: (time: string) => void) => (time: Dayjs | null) => {
    if (!time) return;
    if (!time.isValid()) {
      // If the time is not valid, set the value to an empty string
      onChange('');
      return;
    }

    const formattedTime = time.toISOString();
    onChange(formattedTime);
  };

  const handleNumberOfVehiclesChange = (onChange: (vehicles: number) => void) => (event) => {
    onChange(parseInt(event.target.value));
  };

  // == Render ====================================================================
  return (
    <Stack mt={1}>
      <Stack direction="column" gap={2}>
        <Box gap={3} display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
          <Controller
            control={control}
            name="entryDate"
            rules={{ required: true }}
            render={({ field }) => (
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                label={t('parking_service.inputs.entry_date')}
                onChange={handleParkingDateChange(field.onChange)}
                minDate={minDate}
              />
            )}
          />
          <Controller
            control={control}
            name="entryTime"
            rules={{ required: true }}
            render={({ field }) => {
              return (
                <TimePicker
                  timezone='America/Tijuana'
                  ampm={false}
                  timeSteps={{ minutes: 30 }}
                  value={timePickerValue}
                  label={t('parking_service.inputs.entry_time')}
                  onChange={handleParkingTimeDateChange(field.onChange)}
                  minTime={minHour}
                  disableIgnoringDatePartForTimeValidation
                />
              );
            }}
          />
        </Box>
        <Box gap={3} display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}>
          <Controller
            control={control}
            name="exitDate"
            rules={{ required: true }}
            render={({ field }) => (
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                disabled={!entryDate}
                label={t('parking_service.inputs.exit_date')}
                minDate={entryDate ? dayjs(entryDate) : dayjs()}
                onChange={handleParkingDateChange(field.onChange)}
              />
            )}
          />

          <Controller
            control={control}
            name="exitTime"
            rules={{ required: true }}
            render={({ field }) => (
              <TimePicker
                timezone='America/Tijuana'
                ampm={false}
                timeSteps={{ minutes: 30 }}
                value={field.value ? dayjs(field.value) : null}
                minTime={entryTime && entryDate === exitDate
                   ? dayjs(entryTime).add(30, 'minutes').tz(TIMEZONE) : undefined}
                label={t('parking_service.inputs.exit_time')}
                onChange={handleParkingTimeDateChange(field.onChange)}
              />
            )}
          />
        </Box>
        <Controller
          control={control}
          name="numberOfVehicles"
          rules={{ min: 1 }}
          render={({ field }) => (
            <TextField
              sx={{ maxWidth: 160, mt: 1 }}
              type="number"
              label={t('parking_service.inputs.number_of_vehicles')}
              value={field.value}
              onChange={handleNumberOfVehiclesChange(field.onChange)}
              inputProps={{ min: 1 }}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};
